/**
 * Cross-domain linker (design §7). Appends `_tmglnk` to outbound links toward
 * allowlisted domains ONLY, and ONLY when marketing consent is granted. Carries
 * raw opaque {vid, sid, ts} (medium confidence). The higher-confidence signed
 * path runs server-side via /r/ (attr-security owns HMAC); pixel never signs.
 */
import { hostnameOf, safeJsonParse, safeJsonStringify } from './util';
import { log } from './debug';

const PARAM = '_tmglnk';
const TTL_MS = 120 * 1000;

interface Linker {
  vid: string;
  sid: string;
  ts: number;
}

/** Read an inbound linker from the URL; returns ids if fresh, and strips the param. */
export function readInbound(): { vid?: string; sid?: string } | null {
  try {
    const u = new URL(location.href);
    const raw = u.searchParams.get(PARAM);
    if (!raw) return null;
    const decoded = safeJsonParse<Linker>(b64decode(raw));
    // Always strip the param from the visible URL.
    u.searchParams.delete(PARAM);
    try {
      history.replaceState(history.state, '', u.toString());
    } catch {
      /* noop */
    }
    if (!decoded || !decoded.ts || Date.now() - decoded.ts > TTL_MS) return null;
    return { vid: decoded.vid, sid: decoded.sid };
  } catch {
    return null;
  }
}

/**
 * Wire click decoration. `allow` = allowlisted hostnames; `getIds` returns the
 * current vid/sid; `marketingOk` gates emission (marketing-tier identifier).
 */
export function wireDecorate(
  allow: string[],
  getIds: () => { vid: string; sid: string },
  marketingOk: () => boolean
): void {
  if (!allow || !allow.length) return;
  try {
    document.addEventListener(
      'click',
      (e) => {
        try {
          if (!marketingOk()) return;
          const a = closestAnchor(e.target as Node | null);
          if (!a || !a.href) return;
          const host = hostnameOf(a.href);
          if (!matches(host, allow)) return;
          const ids = getIds();
          if (!ids.vid) return;
          a.href = appendLinker(a.href, ids);
          log('crossdomain decorate', host);
        } catch {
          /* noop */
        }
      },
      true
    );
  } catch {
    /* noop */
  }
}

function appendLinker(href: string, ids: { vid: string; sid: string }): string {
  try {
    const u = new URL(href, location.href);
    const payload: Linker = { vid: ids.vid, sid: ids.sid, ts: Date.now() };
    u.searchParams.set(PARAM, b64encode(safeJsonStringify(payload)));
    return u.toString();
  } catch {
    return href;
  }
}

function matches(host: string, allow: string[]): boolean {
  if (!host) return false;
  for (const d of allow) {
    if (host === d || host.endsWith('.' + d)) return true;
  }
  return false;
}

function closestAnchor(n: Node | null): HTMLAnchorElement | null {
  let el = n as HTMLElement | null;
  while (el) {
    if (el.tagName === 'A') return el as HTMLAnchorElement;
    el = el.parentElement;
  }
  return null;
}

function b64encode(s: string): string {
  try {
    return btoa(unescape(encodeURIComponent(s)));
  } catch {
    return '';
  }
}
function b64decode(s: string): string {
  try {
    return decodeURIComponent(escape(atob(s)));
  } catch {
    return '';
  }
}
